import React, { useState, useEffect, useCallback, useMemo } from 'react';
import colors from '@albertsons/uds/foundation/color';
import Button from '@albertsons/uds/molecule/Button';
import getCount, { AuditFetchResponse, FileMetaDataDTO, fetchCsvFiles, FetchCsvFilesBatchRequest, FetchCsvFileItem } from '../services/GetCount';
import './Dashboard.css';
import CircularProgress from '@mui/material/CircularProgress';
import { DefaultizedPieValueType } from '@mui/x-charts/models';
import { PieChart, pieArcLabelClasses } from '@mui/x-charts/PieChart';
import { DataTable } from './DataTable';
import { normalizeCapabilityKey } from '../services/settingsServices/userRolesService';
import { broadcastDCChange } from '../utils/dcEvents';
import { useUserPermissions } from '../context/UserPermissionsContext';

const Dashboard: React.FC = () => {
    const dayValue = ['1hr', '4hrs', '12hrs', '1 day', '2 days', '3 days', '1 week', '2 weeks', '1 month'];
    const [selectedDay, setSelectedDay] = useState('1 day');
    
    // Define chart categories and types that can be expanded in the future
    const chartCategories = useMemo(() => [
        {
            categoryKey: 'dcFileUpload',
            categoryLabel: 'DC File Upload',
            charts: [
                { key: 'XDOCK', label: 'XDock Orders', color: { success: 'green', partial: '#B8860B', failed: 'red' } },
                { key: 'IN02', label: 'Shipment Planning Upload (IN02)', color: { success: 'green', partial: '#B8860B', failed: 'red' } },
            ]
        },
        {
            categoryKey: 'storeOrder',
            categoryLabel: 'Store Order',
            charts: [
                { key: 'storeOrder', label: 'Store Orders', color: { success: '#2196F3', partial: '#B8860B', failed: '#FF9800' } },
                // Future store order related charts can be added here
            ]
        },
    ], []);
    
    // Flatten all chart types for easier processing
    const allChartTypes = useMemo(() => {
        return chartCategories.flatMap(category => category.charts);
    }, [chartCategories]);
    
    // State for multiple charts
    const [chartData, setChartData] = useState<Map<string, Map<string, string>>>(new Map());
    const [selectedDC, setSelectedDC] = useState("");
    const { userPermissions } = useUserPermissions();
    const [isLoading, setIsLoading] = useState<Map<string, boolean>>(new Map());
    const [pieChartData, setPieChartData] = useState<Map<string, { id: number; value: number; label: string; color: string }[]>>(new Map());
    const [fileMetaData, setFileMetaData] = useState<Map<string, FileMetaDataDTO[]>>(new Map());
    const [page, setPage] = useState<Map<string, number>>(new Map());
    const [order, setOrder] = useState<Map<string, 'asc' | 'desc'>>(new Map());
    const [orderBy, setOrderBy] = useState<Map<string, string>>(new Map());
    const [filters, setFilters] = useState<Map<string, Map<string, string>>>(new Map());
    const [selectedRows, setSelectedRows] = useState<Map<string, Set<number>>>(new Map());
    const [searchColumn, setSearchColumn] = useState<Map<string, string>>(new Map());
    const [showSearch, setShowSearch] = useState<Map<string, boolean>>(new Map());
    const rowsPerPage = 8;
    const [sizing] = useState({
        margin: { right: 5 },
        width: 350,
        height: 140,
        legend: { hidden: true },
    });
    
    const resetTableStates = () => {
        setOrder(new Map());
        setOrderBy(new Map());
        setPage(new Map());
        setFilters(new Map());
        setSearchColumn(new Map());
        setShowSearch(new Map());
        setSelectedRows(new Map());
    };

    const clearTableStateForChart = (chartKey: string) => {
        const deleteKey = (prev: Map<string, any>) => {
            const newMap = new Map(prev);
            newMap.delete(chartKey);
            return newMap;
        };
        setShowSearch(deleteKey);
        setSearchColumn(deleteKey);
        setSelectedRows(deleteKey);
        setOrder(deleteKey);
        setOrderBy(deleteKey);
        // Also clear per-chart filters (so multi-filters are removed when user clicks clear)
        setFilters(deleteKey);
    };
    
    // Check if user has a capability. Normalized (spaces/underscores/hyphens stripped)
    // so keys like 'camsrouting' still match API values like 'CAMS Routing'.
    const hasCapability = useCallback((capability: string): boolean => {
        if (!userPermissions) return false;
        const target = normalizeCapabilityKey(capability);
        return userPermissions.roles
            .filter(role => role.dcCode === selectedDC)
            .some(role =>
                (role.capabilities || []).some(cap => normalizeCapabilityKey(cap) === target)
            );
    }, [userPermissions, selectedDC]);

    // DC list comes from the roles array (unique dcCodes)
    const filteredGroups = useMemo(() => {
        if (!userPermissions) return [];
        const dcSet = new Set<string>();
        (userPermissions.roles || []).forEach(role => {
            if (role.dcCode) dcSet.add(role.dcCode);
        });
        return Array.from(dcSet).sort();
    }, [userPermissions]);

    const storedDC = sessionStorage.getItem('selectedDC') || "";

    // On a fresh login (no DC already stored in this session), default to the
    // user's preferredDc if it's one of their authorized DCs; only fall back to
    // the first authorized DC when there's no valid preferred DC to use.
    const preferredDc = userPermissions?.preferredDc;
    const defaultDC = useMemo(() => {
        if (preferredDc && filteredGroups.includes(preferredDc)) return preferredDc;
        return filteredGroups[0];
    }, [preferredDc, filteredGroups]);

    useEffect(() => {
        if (!selectedDC && filteredGroups.length > 0 && !storedDC) {
            setSelectedDC(defaultDC);
            sessionStorage.setItem('selectedDC', defaultDC);
            broadcastDCChange(defaultDC);
        }
        else if (storedDC && !selectedDC) {
            if (filteredGroups.includes(storedDC)) {
                setSelectedDC(storedDC);
            } else if (filteredGroups.length > 0) {
                setSelectedDC(defaultDC);
                sessionStorage.setItem('selectedDC', defaultDC);
                broadcastDCChange(defaultDC);
            }
        }
        else if (selectedDC) {
            sessionStorage.setItem('selectedDC', selectedDC);
            broadcastDCChange(selectedDC);
        }
    }, [filteredGroups, selectedDC, storedDC, defaultDC]);

    const [selectedTimePeriod, setSelectedTimePeriod] = useState(selectedDay);
    
    const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newSelectedDay = event.target.value;
        setSelectedDay(newSelectedDay);
        setSelectedTimePeriod(newSelectedDay);
        
        if (selectedDC.startsWith('W')) {
            resetTableStates();
            fetchCountData(selectedDC, newSelectedDay);
        }
    };

    const handleDCChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newDC = event.target.value;
        setSelectedDC(newDC);
        // Call API when dropdown changes - pass raw time period string
        if (newDC.startsWith('W')) {
            resetTableStates();
            fetchCountData(newDC, selectedTimePeriod);
        }
    };

    const fetchCountData = useCallback((dc: string, timePeriod: string) => {
        if (dc.startsWith('W')) {
            // Filter chart types based on user capabilities
            const authorizedChartTypes = allChartTypes.filter(chartType =>
                hasCapability(chartType.key)
            );

            authorizedChartTypes.forEach((chartType) => {
                setIsLoading(prev => new Map(prev.set(chartType.key, true)));

                getCount(dc, chartType.key, timePeriod, () => {}, (response: AuditFetchResponse) => {
                    const countMap = new Map();
                    countMap.set("Success", response.successCount.toString());
                    countMap.set("Partial", response.partialProcessCount.toString());
                    countMap.set("Failed", response.errorCount.toString());
                    setChartData(prev => new Map(prev.set(chartType.key, countMap)));
                    setFileMetaData(prev => new Map(prev.set(chartType.key, response.fileMetaDataList)));
                }).finally(() => {
                    setIsLoading(prev => new Map(prev.set(chartType.key, false)));
                });
            });
        }
    }, [allChartTypes, hasCapability]);

    const handleRequestSort = (chartKey: string, property: string) => {
        const currentOrder = order.get(chartKey) || 'asc';
        const currentOrderBy = orderBy.get(chartKey) || '';
        const isAsc = currentOrderBy === property && currentOrder === 'asc';
        setOrder(prev => new Map(prev.set(chartKey, isAsc ? 'desc' : 'asc')));
        setOrderBy(prev => new Map(prev.set(chartKey, property)));
    };

    const handlePageChange = (chartKey: string, newPage: number) => {
        setPage(prev => new Map(prev.set(chartKey, newPage)));
    };

    // No automatic API calls - user must manually trigger via dropdown or refresh button

    useEffect(() => {
        // Process chart data for all chart types
        const newPieChartData = new Map();
        
        allChartTypes.forEach((chartType) => {
            const count = chartData.get(chartType.key);
            if (count) {
                const tempData = [
                    { 
                        id: 0, 
                        value: parseInt(count.get("Success") || "0", 10), 
                        label: 'Success', 
                        color: chartType.color.success 
                    },
                    { 
                        id: 1, 
                        value: parseInt(count.get("Partial") || "0", 10), 
                        label: 'Partial', 
                        color: chartType.color.partial 
                    },
                    { 
                        id: 2, 
                        value: parseInt(count.get("Failed") || "0", 10), 
                        label: 'Failed', 
                        color: chartType.color.failed 
                    },
                ].filter(item => item.value > 0);
                newPieChartData.set(chartType.key, tempData);
            }
        });
        
        setPieChartData(newPieChartData);
    }, [chartData, allChartTypes]);

    const getArcLabel = (params: DefaultizedPieValueType) => `${params.value}`;



    const sortedFileMetaData = useMemo(() => {
        const result = new Map<string, FileMetaDataDTO[]>();
        allChartTypes.forEach(chartType => {
            let data = fileMetaData.get(chartType.key) || [];
            const chartFilters = filters.get(chartType.key) || new Map();
            if (chartFilters.size > 0) {
                data = data.filter(row => {
                    return Array.from(chartFilters.entries()).every(([key, value]) => {
                        if (!value.trim()) return true;
                        return (row as any)[key]?.toString().toLowerCase().includes(value.toLowerCase());
                    });
                });
            }
            const currentOrder = order.get(chartType.key) || 'asc';
            const currentOrderBy = orderBy.get(chartType.key) || '';
            if (currentOrderBy) {
                data = [...data].sort((a: any, b: any) => {
                    if (a[currentOrderBy] < b[currentOrderBy]) return currentOrder === 'asc' ? -1 : 1;
                    if (a[currentOrderBy] > b[currentOrderBy]) return currentOrder === 'asc' ? 1 : -1;
                    return 0;
                });
            }
            result.set(chartType.key, data);
        });
        return result;
    }, [fileMetaData, order, orderBy, filters, allChartTypes]);

    return (
        <div>
            <div className='h-[10px] w-full px-7' style={{ backgroundColor: colors['gray-208'] }}>
                <div className='flex items-center mt-1'>
                    <span className='md:text-[28px] text-[24px] whitespace-nowrap font-bold '>
                        WMS Operations Hub
                    </span>
                    <div className='dashboardDropDownContainer'>
                        <select
                            value={selectedDC}
                            onChange={handleDCChange}
                            disabled={filteredGroups.length === 1}
                            className={`customSelect ${filteredGroups.length === 1 ? 'disabled' : 'enabled'}`}
                        >
                            <option value="">Select DC</option>
                            {filteredGroups.map((dc: string) => (
                                <option key={dc} value={dc}>{dc}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>
            <div className='h-[15px] w-full px-7' style={{ backgroundColor: colors['gray-208'] }}>
                <div className='flex items-center mt-2'>
                    <span className='md:text-[22px] text-[22px] whitespace-nowrap font-bold '>
                        Dashboard
                    </span>
                </div>
            </div>
            <div className='dashboardBox'>
                <div className='dashboardInnerBox'>
                    Number of Orders loaded in last :
                    <select className='innerdropdown' value={selectedDay} onChange={handleChange}>
                        {dayValue.map((group: string) => (
                            <option key={group} value={group}>{group}</option>
                        ))}
                    </select>
                    <Button className='refresh-button' disabled={!selectedDC.startsWith('W') || Array.from(isLoading.values()).some(loading => loading)} onClick={() => { resetTableStates(); fetchCountData(selectedDC, selectedTimePeriod); }}>Refresh</Button >
                    {Array.from(isLoading.values()).some(loading => loading) && <CircularProgress style={{ marginLeft: '10px' }} />}
                </div>
                
                {/* Render charts without categories */}
                <div className="charts-container" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', gap: '0px', maxWidth: '100%' }}>
                    {!selectedDC ? (
                        <p>Please select a DC to view the data.</p>
                    ) : (
                        (() => {
                            // Filter chart types based on user capabilities
                            const authorizedChartTypes = allChartTypes.filter(chartType =>
                                hasCapability(chartType.key)
                            );

                            if (authorizedChartTypes.length === 0) {
                                return <p>No authorized charts available for the selected DC.</p>;
                            }

                            return authorizedChartTypes.map((chartType) => {
                                const chartCount = chartData.get(chartType.key);
                                const data = pieChartData.get(chartType.key) || [];
                                const isChartLoading = isLoading.get(chartType.key) || false;
                                const successCount = parseInt(chartCount?.get("Success") || "0", 10);
                                const partialCount = parseInt(chartCount?.get("Partial") || "0", 10);
                                const failedCount = parseInt(chartCount?.get("Failed") || "0", 10);
                                const hasData = successCount > 0 || partialCount > 0 || failedCount > 0;
                                const metaData = sortedFileMetaData.get(chartType.key) || [];

                                // Multi-filter values live in `filters[chartKey][columnKey]`.
                                // The TextField should display ONLY the active column's value.
                                const activeColumn = searchColumn.get(chartType.key) || 'fileName';
                                const activeFilters = filters.get(chartType.key) || new Map<string, string>();
                                const inputValueForActiveColumn = activeFilters.get(activeColumn) || '';

                                return (
                                    <div key={chartType.key} className="chart-section" style={{ 
                                        display: 'flex', 
                                        flexDirection: 'row', 
                                        alignItems: 'flex-start', 
                                        gap: '0px', 
                                        width: '100%', 
                                        minWidth: '600px',
                                        maxWidth: '2000px',
                                        marginBottom: '8px'
                                    }}>
                                        <div className="pie-chart" style={{ flex: 1 }}>
                                            <h3 style={{ marginBottom: '15px', fontSize: '20px', fontWeight: 'bold' }}>
                                                {chartType.label}
                                            </h3>
                                            <div className="chart-content">
                                                {isChartLoading ? (
                                                    <div style={{ height: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <CircularProgress />
                                                    </div>
                                                ) : !hasData ? (
                                                    <div style={{ height: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <p>No data matching the given condition.</p>
                                                    </div>
                                                ) : (
                                                    <PieChart
                                                        series={[
                                                            {
                                                                innerRadius: 40,
                                                                outerRadius: 70,
                                                                data,
                                                                arcLabel: getArcLabel,
                                                            },
                                                        ]}
                                                        sx={{
                                                            [`& .${pieArcLabelClasses.root}`]: {
                                                                fill: 'white',
                                                                fontSize: 12,
                                                                fontWeight: 'bold',
                                                            },
                                                        }}
                                                        {...sizing}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                        <div className="table-container">
                                            <DataTable
                                                chartKey={chartType.key}
                                                data={metaData}
                                                orderBy={orderBy.get(chartType.key)}
                                                order={order.get(chartType.key)}
                                                showSearch={showSearch.get(chartType.key)}
                                                searchColumn={searchColumn.get(chartType.key)}
                                                searchTerm={inputValueForActiveColumn}
                                                currentPage={page.get(chartType.key) || 0}
                                                rowsPerPage={rowsPerPage}
                                                selectedRows={selectedRows.get(chartType.key) || new Set()}
                                                onSort={(property) => handleRequestSort(chartType.key, property)}
                                                onSearchClick={(column) => {
                                                    setShowSearch(prev => new Map(prev.set(chartType.key, true)));
                                                    setSearchColumn(prev => new Map(prev.set(chartType.key, column)));
                                                }}
                                                onSearchChange={(value) => {
                                                    const col = searchColumn.get(chartType.key) || 'fileName';
                                                    setFilters(prev => {
                                                        const next = new Map(prev);
                                                        const perChart = new Map(next.get(chartType.key) || []);
                                                        perChart.set(col, value);
                                                        next.set(chartType.key, perChart);
                                                        return next;
                                                    });
                                                }}
                                                onSearchBlur={() => {
                                                    setShowSearch(prev => new Map(prev.set(chartType.key, false)));
                                                }}
                                                onPageChange={(newPage) => handlePageChange(chartType.key, newPage)}
                                                onRowClick={(index) => {
                                                    const chartSelected = selectedRows.get(chartType.key) || new Set();
                                                    const newSelected = new Set(chartSelected);
                                                    if (newSelected.has(index)) {
                                                        newSelected.delete(index);
                                                    } else {
                                                        newSelected.add(index);
                                                    }
                                                    setSelectedRows(prev => new Map(prev.set(chartType.key, newSelected)));
                                                }}
                                                onDownloadAll={() => {

                                                    const items: FetchCsvFileItem[] = metaData.map(row => ({
                                                        applicationName: chartType.key,
                                                        filename: row.fileName,
                                                        status: row.status
                                                    }));

                                                    const request: FetchCsvFilesBatchRequest = { 
                                                        items 
                                                    };

                                                    fetchCsvFiles(selectedDC, request);
                                                    
                                                    // Clear selected rows after download
                                                    setSelectedRows(prev => new Map(prev.set(chartType.key, new Set())));

                                                }}

                                                onDownloadSelected={() => {

                                                    const selected = selectedRows.get(chartType.key) || new Set();

                                                    const selectedData = metaData.filter((_, index) => selected.has(index));

                                                    const items: FetchCsvFileItem[] = selectedData.map(row => ({
                                                        applicationName: chartType.key,
                                                        filename: row.fileName,
                                                        status: row.status
                                                    }));

                                                    const request: FetchCsvFilesBatchRequest = { 
                                                        items 
                                                    };
                                                    fetchCsvFiles(selectedDC, request);                       
                                                    // Clear selected rows after download
                                                    setSelectedRows(prev => new Map(prev.set(chartType.key, new Set())));
                                                }}
                                                onClearFilter={() => clearTableStateForChart(chartType.key)}
                                            />
                                        </div>
                                    </div>
                                );
                            });
                        })()
                    )}
                </div>
            </div>
        </div >
    );
};

export default Dashboard;
